/**
 * Social Share Buttons Component
 *
 * Reusable component for sharing content on social media platforms
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import {
  ShareData,
  shareToFacebook,
  shareToTwitter,
  shareToWhatsApp,
  shareViaEmail,
  shareToLinkedIn,
  shareToTelegram,
  copyForInstagram,
  copyForTikTok,
  copyToClipboard,
  nativeShare,
  isNativeShareAvailable,
  getShareText
} from '@/utils/socialShareUtils';
import {
  Share2,
  Facebook,
  Twitter,
  MessageCircle,
  Mail,
  Linkedin,
  Send,
  Instagram,
  Music2,
  Link as LinkIcon,
  Check
} from 'lucide-react';

interface SocialShareButtonsProps {
  url: string;
  title: string;
  text?: string;
  compact?: boolean;
  showLabels?: boolean;
  className?: string;
}

export const SocialShareButtons: React.FC<SocialShareButtonsProps> = ({
  url,
  title,
  text,
  compact = false,
  showLabels = true,
  className = ''
}) => {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);

  const shareData: ShareData = {
    url,
    title,
    text: text || getShareText(title)
  };

  const handleCopyLink = async () => {
    const success = await copyToClipboard(url);

    if (success) {
      setCopied(true);
      toast({
        title: 'Link Copied!',
        description: 'Share this link with anyone.',
        duration: 2000
      });
      setTimeout(() => setCopied(false), 2000);
    } else {
      toast({
        title: 'Copy Failed',
        description: 'Please copy the link manually.',
        variant: 'destructive'
      });
    }
  };

  const handleNativeShare = async () => {
    const success = await nativeShare(shareData);

    if (!success) {
      toast({
        title: 'Share Unavailable',
        description: 'Please use the individual share buttons below.',
        variant: 'destructive'
      });
    }
  };

  const handleInstagramShare = async () => {
    const result = await copyForInstagram(url);

    toast({
      title: result.success ? 'Ready for Instagram!' : 'Copy Failed',
      description: result.message,
      variant: result.success ? 'default' : 'destructive',
      duration: 4000
    });
  };

  const handleTikTokShare = async () => {
    const result = await copyForTikTok(url);

    toast({
      title: result.success ? 'Ready for TikTok!' : 'Copy Failed',
      description: result.message,
      variant: result.success ? 'default' : 'destructive',
      duration: 4000
    });
  };

  const handleFacebookShare = () => {
    shareToFacebook(shareData);
    toast({
      title: 'Opening Facebook',
      description: 'Share window will open shortly.',
      duration: 2000
    });
  };

  const handleTwitterShare = () => {
    const twitterData = {
      ...shareData,
      text: getShareText(title, 'twitter')
    };
    shareToTwitter(twitterData);
    toast({
      title: 'Opening Twitter',
      description: 'Share window will open shortly.',
      duration: 2000
    });
  };

  const handleWhatsAppShare = () => {
    const whatsappData = {
      ...shareData,
      text: getShareText(title, 'whatsapp')
    };
    shareToWhatsApp(whatsappData);
    toast({
      title: 'Opening WhatsApp',
      description: 'Share window will open shortly.',
      duration: 2000
    });
  };

  const handleEmailShare = () => {
    shareViaEmail(shareData);
  };

  const handleLinkedInShare = () => {
    shareToLinkedIn(shareData);
    toast({
      title: 'Opening LinkedIn',
      description: 'Share window will open shortly.',
      duration: 2000
    });
  };

  const handleTelegramShare = () => {
    shareToTelegram(shareData);
    toast({
      title: 'Opening Telegram',
      description: 'Share window will open shortly.',
      duration: 2000
    });
  };

  const buttonSize = compact ? 'sm' : 'default';
  const iconSize = compact ? 'h-4 w-4' : 'h-5 w-5';

  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {/* Copy Link Button - Primary Action */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleCopyLink}
        className="flex-1 min-w-[140px]"
      >
        {copied ? (
          <>
            <Check className={`${iconSize} mr-2`} />
            {showLabels && 'Copied!'}
          </>
        ) : (
          <>
            <LinkIcon className={`${iconSize} mr-2`} />
            {showLabels && 'Copy Link'}
          </>
        )}
      </Button>

      {/* Native Share Button (mobile) */}
      {isNativeShareAvailable() && (
        <Button
          variant="outline"
          size={buttonSize}
          onClick={handleNativeShare}
          className="flex-1 min-w-[140px]"
        >
          <Share2 className={`${iconSize} mr-2`} />
          {showLabels && 'Share'}
        </Button>
      )}

      {/* Instagram */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleInstagramShare}
        className="flex-1 min-w-[120px]"
      >
        <Instagram className={`${iconSize} mr-2`} />
        {showLabels && 'Instagram'}
      </Button>

      {/* TikTok */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleTikTokShare}
        className="flex-1 min-w-[120px]"
      >
        <Music2 className={`${iconSize} mr-2`} />
        {showLabels && 'TikTok'}
      </Button>

      {/* Facebook */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleFacebookShare}
        className="flex-1 min-w-[120px]"
      >
        <Facebook className={`${iconSize} mr-2`} />
        {showLabels && 'Facebook'}
      </Button>

      {/* Twitter/X */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleTwitterShare}
        className="flex-1 min-w-[120px]"
      >
        <Twitter className={`${iconSize} mr-2`} />
        {showLabels && 'Twitter'}
      </Button>

      {/* WhatsApp */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleWhatsAppShare}
        className="flex-1 min-w-[120px]"
      >
        <MessageCircle className={`${iconSize} mr-2`} />
        {showLabels && 'WhatsApp'}
      </Button>

      {/* Email */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleEmailShare}
        className="flex-1 min-w-[120px]"
      >
        <Mail className={`${iconSize} mr-2`} />
        {showLabels && 'Email'}
      </Button>

      {/* LinkedIn */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleLinkedInShare}
        className="flex-1 min-w-[120px]"
      >
        <Linkedin className={`${iconSize} mr-2`} />
        {showLabels && 'LinkedIn'}
      </Button>

      {/* Telegram */}
      <Button
        variant="outline"
        size={buttonSize}
        onClick={handleTelegramShare}
        className="flex-1 min-w-[120px]"
      >
        <Send className={`${iconSize} mr-2`} />
        {showLabels && 'Telegram'}
      </Button>
    </div>
  );
};

export default SocialShareButtons;
